// src/utils/buildHandoffModel.js
//
// Pure model for the Insurance History Handoff PDF — what the next agent needs
// to pull loss runs and understand the account. Built from an EXPLICIT field
// allowlist: nothing is copied from a row wholesale, so premiums, fees, taxes,
// commissions, FEIN, vault paths, minutes and internal notes cannot reach the
// document. buildHandoffModel.test.js walks every key to prove it.
//
// Blank values print as "Not on record", never a guess. Submitted/Quoted rows
// are not policies and are excluded. A source with DoNotContact true never gets
// a contact block or instruction; an agency named in a policy's AgencyOfRecord
// still prints there as plain text, because that is a fact about the policy.

import { dateOnlyOf, isLossRunEligible, termOf, boolOrNull, authorizationState, authorizationNeed, sortRequestsNewestFirst, requestCoversPolicy } from './lossRunStatus';
import { parseLocalDate } from './parseLocalDate';

export const NOT_ON_RECORD = 'Not on record';
export const DIRECT_WITH_CARRIER = 'Direct with carrier';
export const PREPARED_BY = 'Fragos Insurance Group';
export const COPIES_LINE = 'Copies available from Fragos Insurance Group on request.';

const blank = v => v === null || v === undefined || String(v).trim() === '';
const text = v => (blank(v) ? NOT_ON_RECORD : String(v).trim());

// Display only: "YYYY-MM-DD" (first 10 chars of whatever was stored) -> "MM/DD/YYYY".
// Never converts to local time; stored values are untouched.
export function displayDate(v) {
  const d = dateOnlyOf(v);
  if (!d) return null;
  const [y, m, day] = d.split('-');
  return `${m}/${day}/${y}`;
}
const dateText = v => displayDate(v) || NOT_ON_RECORD;

function effectiveTime(p) {
  const d = parseLocalDate(p?.EffectiveDate);
  return d ? d.getTime() : null;
}
const byNewest = (a, b) => {
  const ta = effectiveTime(a), tb = effectiveTime(b);
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  return tb - ta;
};

export function handoffFilename(insured, today) {
  const name = String(insured?.Title || insured?.CompanyName || 'Insured').trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'Insured';
  return `${name}_Insurance_History_Handoff_${dateOnlyOf(today) || dateOnlyOf(new Date())}.pdf`;
}

/**
 * buildHandoffModel({ insured, policies, sources, requests, today })
 *   policies / requests — this client's rows (already filtered by InsuredId)
 */
export function buildHandoffModel({ insured = {}, policies = [], sources = [], requests = [], today } = {}) {
  const todayKey = dateOnlyOf(today) || dateOnlyOf(new Date());
  const eligible = (policies || []).filter(isLossRunEligible);

  // 1. Header — allowlisted insured fields only. No FEIN.
  const addrLine = [insured.MailingAddress, [insured.MailingCity, insured.MailingState].filter(v => !blank(v)).join(', '), insured.MailingZip]
    .filter(v => !blank(v)).map(v => String(v).trim()).join(' · ');
  const header = {
    insuredName: text(insured.Title || insured.CompanyName),
    dba: blank(insured.DBA) ? null : String(insured.DBA).trim(),
    mailingAddress: addrLine || NOT_ON_RECORD,
    preparedDate: displayDate(todayKey),
    preparedBy: PREPARED_BY,
  };

  const sourceById = new Map((sources || []).map(s => [String(s.id), s]));
  const sourceOf = p => (blank(p.LossRunSourceId) ? null : sourceById.get(String(p.LossRunSourceId).trim()) || null);
  // Placed through: the wholesaler when known; "Direct with carrier" when the
  // wholesaler is blank and the policy's loss-run source is a Carrier.
  const placedThrough = p => {
    if (!blank(p.WholesaleBroker)) return String(p.WholesaleBroker).trim();
    const s = sourceOf(p);
    return s && String(s.Kind || '').trim().toLowerCase() === 'carrier' ? DIRECT_WITH_CARRIER : NOT_ON_RECORD;
  };

  // 2. Policies by term, newest first.
  const termMap = new Map();
  for (const p of eligible) {
    const t = termOf(p) || 'Term not on record';
    if (!termMap.has(t)) termMap.set(t, []);
    termMap.get(t).push(p);
  }
  const terms = [...termMap.entries()]
    .sort((a, b) => (a[0] === 'Term not on record') - (b[0] === 'Term not on record') || b[0].localeCompare(a[0]))
    .map(([term, list]) => ({
      term,
      policies: [...list].sort(byNewest).map(p => ({
        line: text(p.LOB),
        carrier: text(p.Carrier),
        policyNumber: text(p.PolicyNumber),
        effective: dateText(p.EffectiveDate),
        expiration: dateText(p.ExpirationDate),
        status: text(p.Status),
        agencyOfRecord: text(p.AgencyOfRecord),
        placedThrough: placedThrough(p),
        admittedStatus: text(p.AdmittedStatus),
      })),
    }));

  // 3. Who to ask — sources with assigned eligible policies, never DoNotContact.
  const assigned = new Map();
  for (const p of eligible) {
    const sid = blank(p.LossRunSourceId) ? null : String(p.LossRunSourceId).trim();
    if (!sid) continue;
    if (!assigned.has(sid)) assigned.set(sid, []);
    assigned.get(sid).push(p);
  }
  const whoToAsk = [];
  for (const [sid, list] of assigned) {
    const s = sourceById.get(sid);
    if (!s) continue;
    if (boolOrNull(s.DoNotContact) === true) continue;     // never a block, detail or instruction
    const need = authorizationNeed(s, list).need;          // same derivation as the card and the Ask
    whoToAsk.push({
      name: text(s.Title),
      kind: text(s.Kind),
      contactName: text(s.ContactName),
      email: text(s.ContactEmail),
      phone: text(s.ContactPhone),
      defaultMethod: text(s.DefaultMethod),
      lines: [...new Set(list.map(p => text(p.LOB)))].sort(),
      terms: [...new Set(list.map(p => termOf(p) || 'Term not on record'))].sort(),
      authorization: need === 'needed' ? 'required' : need === 'not needed' ? 'not required' : 'unknown',
    });
  }
  whoToAsk.sort((a, b) => a.name.localeCompare(b.name));

  // 4. Loss runs on file / open requests. No VaultPath, MinutesSpent or Notes.
  //    Coverage uses the per-policy matching (PolicyIds blank = every eligible
  //    policy of the source; otherwise only the listed ids).
  const sourceName = r => text(sourceById.get(String(r.LossRunSourceId ?? '').trim())?.Title);
  const coverageOf = r => {
    const srcPols = eligible.filter(p => String(p.LossRunSourceId ?? '').trim() === String(r.LossRunSourceId ?? '').trim());
    const covered = srcPols.filter(p => requestCoversPolicy(r, p));
    if (srcPols.length === 0) return NOT_ON_RECORD;
    if (covered.length === srcPols.length) return `All ${srcPols.length} ${sourceName(r)} polic${srcPols.length === 1 ? 'y' : 'ies'}`;
    return `${covered.length} of ${srcPols.length}: ${covered.map(p => text(p.PolicyNumber)).join(', ') || NOT_ON_RECORD}`;
  };
  const ordered = sortRequestsNewestFirst(requests || []);
  const onFile = ordered.filter(r => dateOnlyOf(r.DateReceived)).map(r => ({
    source: sourceName(r),
    method: text(r.Method),
    termsCovered: text(r.TermsCovered),
    coverage: coverageOf(r),
    dateReceived: dateText(r.DateReceived),
    valuedAsOf: dateText(r.ValuedAsOf),
  }));
  const open = ordered.filter(r => !dateOnlyOf(r.DateReceived)).map(r => ({
    source: sourceName(r),
    method: text(r.Method),
    termsCovered: text(r.TermsCovered),
    coverage: coverageOf(r),
    requestedLine: displayDate(r.DateRequested) ? `requested ${displayDate(r.DateRequested)}, not yet received` : 'requested, date not on record, not yet received',
  }));

  // 5. Authorization letter — no vault path.
  const auth = authorizationState(insured, todayKey);
  const authorization = {
    onFile: auth.onFile === true ? 'yes' : auth.onFile === false ? 'no' : 'unknown',
    signedDate: displayDate(auth.signed) || NOT_ON_RECORD,
  };

  return {
    header,
    terms,
    policyCount: eligible.length,
    whoToAsk,
    onFile,
    open,
    copiesLine: COPIES_LINE,
    authorization,
    filename: handoffFilename(insured, todayKey),
  };
}
